import React, { useState } from "react";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import moment from "moment";
import Modal1 from "./Modal1";
import PrimaryButton from "../buttons/PrimaryButton";
import SecondaryButton from "../buttons/SecondaryButton";
import TextField2T from "../textfields/TextField2T";
import SmallSpinner from "../loading/SmallSpinner";
import { useSnackbar } from "notistack";
import { queryCollection } from "../models/DataFunctions";
import { queryFolders } from "../models/AlgoliaFunctions";

const buildAddress = (infos) => {
  if (!infos) return "";
  return [infos.adresse, infos.postal, infos.ville]
    .filter((p) => p && String(p).trim() !== "")
    .join(", ");
};

const buildLastControlByFolderId = (controls) => {
  const map = new Map();
  for (const control of controls) {
    const folderId = control?.folder_ref?.id;
    const seconds = control?.date?.seconds;
    if (!folderId || !seconds) continue;
    const current = map.get(folderId);
    if (current === undefined || seconds > current) {
      map.set(folderId, seconds);
    }
  }
  return map;
};

const findLastRdv = (partnerId, folders, lastControlByFolderId) => {
  let best = null;
  for (const folder of folders) {
    const linked =
      (folder.partners_id || []).includes(partnerId) ||
      folder.client_id === partnerId;
    if (!linked) continue;
    const seconds = lastControlByFolderId.get(folder.id);
    if (!seconds) continue;
    if (best === null || seconds > best) best = seconds;
  }
  return best;
};

function ExtrairePartnersModal({
  isOpen,
  onClose,
  partners,
  workspaces,
  orgData,
  defaultPrenom,
}) {
  const [commercialFilter, setCommercialFilter] = useState(defaultPrenom || "");
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleExtract = async () => {
    const filter = (commercialFilter || "").trim().toLowerCase();
    if (!filter) {
      enqueueSnackbar("Veuillez renseigner un commercial", { variant: "error" });
      return;
    }

    setLoading(true);
    try {
      const matching = partners.filter((partner) => {
        const workspace = workspaces.find((w) => w.partner_id === partner.id);
        const commercial = workspace?.commercial_infos?.commercial_client || "";
        return commercial.toLowerCase().includes(filter);
      });

      if (matching.length === 0) {
        enqueueSnackbar(
          `Aucun partenaire trouvé pour le commercial "${commercialFilter}"`,
          { variant: "warning" }
        );
        setLoading(false);
        return;
      }

      const [controls, folders] = await Promise.all([
        queryCollection("control", [
          { field: "org_id", operator: "==", value: orgData?.id },
        ]),
        queryFolders(null, orgData, []),
      ]);
      const lastControlByFolderId = buildLastControlByFolderId(controls);

      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("Partenaires");

      sheet.columns = [
        { header: "Entreprise", key: "name", width: 35 },
        { header: "Adresse", key: "address", width: 45 },
        { header: "Personne qui gère", key: "contact", width: 25 },
        { header: "Téléphone", key: "phone", width: 18 },
        { header: "Dernier RDV", key: "last_rdv", width: 15 },
      ];

      sheet.getRow(1).font = { bold: true };

      matching.forEach((partner) => {
        const workspace = workspaces.find((w) => w.partner_id === partner.id);
        const ci = workspace?.commercial_infos || {};
        const lastRdvSeconds = findLastRdv(
          partner.id,
          folders,
          lastControlByFolderId
        );
        sheet.addRow({
          name: partner.name || "",
          address: buildAddress(partner.infos),
          contact: ci.nom_respo || "",
          phone: ci.phone_respo || partner.phone || "",
          last_rdv: lastRdvSeconds
            ? moment(lastRdvSeconds * 1000).format("DD/MM/YYYY")
            : "",
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const fileName = `Partenaires_${commercialFilter.trim()}_${moment().format(
        "DDMMYY_HHmmss"
      )}.xlsx`;
      saveAs(blob, fileName);
      enqueueSnackbar(`${matching.length} partenaire(s) extrait(s)`, {
        variant: "success",
      });
      onClose();
    } catch (error) {
      console.error("Error extracting partners:", error);
      enqueueSnackbar("Erreur lors de l'extraction", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal1 isOpen={isOpen} onClose={onClose}>
      <div className="bg-white flex flex-col w-c400 rounded-3xl">
        <div className="border-b-1 p-4 text-h3">Extraire les partenaires</div>
        <div className="p-4 flex flex-col gap-3">
          <div className="text-h3 text-shadow_50">
            Filtrer par commercial qui s&apos;occupe du client.
          </div>
          <TextField2T
            label="Commercial"
            value={commercialFilter}
            onChange={(e) => setCommercialFilter(e.target.value)}
          />
        </div>
        <div className="flex flex-row gap-2 justify-end border-t-1 p-4">
          {loading ? (
            <SmallSpinner />
          ) : (
            <>
              <SecondaryButton onClick={onClose}>Annuler</SecondaryButton>
              <PrimaryButton onClick={handleExtract}>Extraire</PrimaryButton>
            </>
          )}
        </div>
      </div>
    </Modal1>
  );
}

export default ExtrairePartnersModal;
